import { ComboboxProvider, Select, SelectProvider } from '@ariakit/react'
import { type FC, useMemo, useState } from 'react'
import { Checkbox } from '@/components/commons/checkbox'
import { Chip } from '@/components/commons/chip'
import { ErrorBox } from '@/components/commons/error-box'
import { Flexbox } from '@/components/ui/flexbox'
import { AlertTriangleIcon, PlusIcon } from '@/components/ui/icons'
import { InputHelpText } from '@/components/ui/input-help-text'
import { SelectComboboxList } from '@/components/ui/select-atoms-legacy'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import {
	OpsVettingFlagsSelectItem,
	OpsVettingFlagsSelectSearchInput,
	OpsVettingFlagsSelectStyledPopover
} from '@/features/ops-vetting/components/ops-vetting-flags-select-atoms'
import { useGetServiceFlags } from '@/features/service-vetting/query/use-get-service-flags.ts'
import type { ServiceFlag } from '@/features/service-vetting/types.ts'
import { useTranslation } from '@/translations/i18n'

const FlagsContent = ({
	isLoading,
	isError,
	flagsList,
	selectedFlagsId,
	onUpdateFlag
}: {
	isLoading: boolean
	isError: boolean
	flagsList: (ServiceFlag & { label: string })[]
	selectedFlagsId: number[]
	onUpdateFlag: (checked: boolean, flag: ServiceFlag) => void
}) => {
	const { t } = useTranslation()
	if (isLoading) {
		return (
			<Flexbox direction='column' gap={1}>
				{Array.from({ length: 3 }, (_, i) => `skeleton-${i}`).map(
					(key) => (
						<Skeleton height={32} key={key} />
					)
				)}
			</Flexbox>
		)
	}
	if (isError) {
		return (
			<ErrorBox
				icon={<AlertTriangleIcon />}
				title={t('errors:default')}
			/>
		)
	}
	if (flagsList.length === 0) {
		return (
			<OpsVettingFlagsSelectItem>
				<InputHelpText helpText={t('common:no_results')} />
			</OpsVettingFlagsSelectItem>
		)
	}
	return flagsList.map((option) => (
		<OpsVettingFlagsSelectItem key={option.id}>
			<Checkbox
				checked={selectedFlagsId.includes(option.id)}
				id={option.id.toString()}
				onChange={(event) => onUpdateFlag(event.target.checked, option)}
			/>
			<p>{option.label}</p>
		</OpsVettingFlagsSelectItem>
	))
}

export interface ServiceVettingCriteriaFlagsSelectProps {
	addFlag: (Flag: ServiceFlag) => void
	removeFlag: (Flag: ServiceFlag) => void
	selectedFlagsId: number[]
	disabled?: boolean
	types?: ServiceFlag['type'][]
}

export const ServiceVettingCriteriaFlagsSelect: FC<
	ServiceVettingCriteriaFlagsSelectProps
> = ({ addFlag, removeFlag, selectedFlagsId, disabled, types }) => {
	const { t } = useTranslation()
	const flagsQuery = useGetServiceFlags({ response_type: 'extended', types })
	const [search, setSearch] = useState('')

	const flagsList = useMemo(() => {
		const remappedFlags = flagsQuery.data
			? flagsQuery.data?.map((data) => ({
					...data,
					label: t(
						`vetting:criteria.service.flags_key_name.${data.key_name}`
					)
				}))
			: []
		return remappedFlags.filter((flag) =>
			flag.label.toLowerCase().includes(search.toLowerCase())
		)
	}, [flagsQuery.data, search, t])

	const handleUpdateFlag = (checked: boolean, flag: ServiceFlag) => {
		checked ? addFlag(flag) : removeFlag(flag)
	}

	return (
		<ComboboxProvider
			resetValueOnHide
			setValue={(search) => setSearch(search)}
			value={search}
		>
			<SelectProvider>
				<Select disabled={disabled}>
					<Chip
						isClickable
						label={'Flags'}
						startSlot={<PlusIcon />}
					/>
				</Select>
				<OpsVettingFlagsSelectStyledPopover unmountOnHide>
					<OpsVettingFlagsSelectSearchInput
						placeholder={t('photoset:search_flags')}
					/>
					<Separator orientation='horizontal' />
					<SelectComboboxList>
						<FlagsContent
							flagsList={flagsList}
							isError={flagsQuery.isError}
							isLoading={flagsQuery.isLoading}
							onUpdateFlag={handleUpdateFlag}
							selectedFlagsId={selectedFlagsId}
						/>
					</SelectComboboxList>
				</OpsVettingFlagsSelectStyledPopover>
			</SelectProvider>
		</ComboboxProvider>
	)
}
